import { StorefrontConfig } from "@redotech/redo-model/team";
import { SellingPlanGroup, WidgetAutocheck } from "@redotech/redo-model/widget";
import { getLocalStorageJson } from "@redotech/redo-web/utils/local-storage-wrapper";
import type { ShopifyCartItem } from "@redotech/shopify-client/ajax";

export const REVIEWS_PDP_BLOCK_TARGET_ID = "redo-reviews-pdp-block-target";

export const isRedoItem = (item: ShopifyCartItem) => {
  return (
    item.vendor?.toLowerCase() === "re:do" ||
    item.title?.toLowerCase().includes("re:do")
  );
};

export const isPackageProtection = (item: ShopifyCartItem) =>
  item.handle === "navidium-shipping-protection" ||
  item.vendor === "Order Protection";

export const containsExcludedItemProperty = (
  item: ShopifyCartItem,
  excludedProperties: { name: string; value: string }[],
): boolean => {
  return excludedProperties.some((property) => {
    return Object.entries(item.properties || {}).some(([key, val]) => {
      return key === property.name && val === property.value;
    });
  });
};

export const REDO_DISABLE_TAGS = ["redo-hidden"];

export const getMatchingRedoSellingPlanId = async (
  redoProduct: { sellingPlanGroups: SellingPlanGroup[] },
  sellingPlans: any[],
) => {
  const redoSellingPlans = redoProduct.sellingPlanGroups.flatMap(
    (group) => group.sellingPlans,
  );

  const allowedIntervals = sellingPlans
    .filter((plan) => plan.billingPolicy.intervalDays)
    .map((plan) => plan.billingPolicy.intervalDays);

  const matchingSellingPlans = redoSellingPlans
    .filter((plan) => plan.billingPolicy.intervalDays)
    .filter((plan) =>
      allowedIntervals.includes(plan.billingPolicy.intervalDays),
    );

  if (matchingSellingPlans.length === 0) {
    return null;
  }

  const highestFrequencySellingPlan = matchingSellingPlans.reduce(
    (acc, plan) =>
      plan.billingPolicy.intervalDays! < acc.billingPolicy.intervalDays!
        ? plan
        : acc,
  );

  try {
    const id = highestFrequencySellingPlan.id.split("/").slice(-1)[0];
    return id ? parseInt(id) : null;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getLocalStorageIgnoreExpiry = (key: string) => {
  return getLocalStorageJson(key);
};

export const getVariantIdFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const variantId = urlObj.searchParams.get("variant");
    return variantId;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
};

export function getCartShadowRoot(
  autocheck: WidgetAutocheck,
  shadowRootSelectorOverride?: string,
): ShadowRoot | undefined {
  const useShadowRoot =
    shadowRootSelectorOverride ?? autocheck.cartToggle.useShadowRootInCart;
  const shadowDomSelector =
    shadowRootSelectorOverride ?? autocheck.cartToggle.cartShadowRootSelector;
  if (!useShadowRoot || !shadowDomSelector) {
    return undefined;
  }
  return document.querySelector(shadowDomSelector)?.shadowRoot ?? undefined;
}

// Query both the document and the shadow root (if it exists) to make sure we get ALL elements that match the selector
export function queryDocumentAndShadowRoot(
  selector: string,
  autocheck: WidgetAutocheck,
): Element[] {
  return [
    ...document.querySelectorAll(selector),
    ...(getCartShadowRoot(autocheck)?.querySelectorAll(selector) ?? []),
  ];
}

/**
 * Gets the appropriate storefront access token for the current hostname
 * If no matching hostname is found in multistorefront configs, returns the default token
 */
export function getStorefrontAccessToken(autocheck: WidgetAutocheck): string {
  // If no multistorefront configuration, just return the default token
  if (!autocheck.multistorefront || autocheck.multistorefront.length === 0) {
    return autocheck.storefrontAccessToken;
  }

  // Get current hostname
  const currentHostname = window.location.hostname;

  // Look for a matching hostname in the multistorefront config
  const matchingConfig = autocheck.multistorefront.find(
    (config: StorefrontConfig) => config.hostname === currentHostname,
  );

  // Return the matching token if found, otherwise the default token
  return matchingConfig
    ? matchingConfig.storefrontAccessToken
    : autocheck.storefrontAccessToken;
}
